import React from "react"
import ReactDOMServer from "react-dom/server"
import { ThemeProvider, Theme } from "@mui/material/styles"
import { CacheProvider } from "@emotion/react"
import { TssCacheProvider } from "tss-react"
import buildEmotionStyleTagsFromHtml from "./buildEmotionStyleTagsFromHtml"
import { createPdfEmotionCache } from "./createPdfEmotionCache"
import { buildPdfPageLayoutCss } from "./buildPdfPageLayoutCss"
import {
  normalizePdfFooterSlot,
  normalizePdfHeaderSlot
} from "./normalizePdfRunningSlot"
import { PdfGeneratorHeaderFooterSlot, PdfGeneratorPageOptions } from "../types"

export type RenderChildrenToHtmlDocumentOptions = {
  content: React.ReactElement
  documentTitle?: string
  theme?: Theme
  stylesheetUrls?: string[]
  cacheKey?: string
  header?: React.ReactElement | PdfGeneratorHeaderFooterSlot
  footer?: React.ReactElement | PdfGeneratorHeaderFooterSlot
  page?: PdfGeneratorPageOptions
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

const renderRunningElement = (
  slot: ReturnType<typeof normalizePdfHeaderSlot>
) => {
  if (!slot) {
    return null
  }

  return (
    <div key={slot.runningId} data-pdf-running={slot.runningId}>
      {slot.content}
    </div>
  )
}

export const renderChildrenToHtmlDocument = (
  args: RenderChildrenToHtmlDocumentOptions
): string => {
  const {
    content,
    documentTitle = "Document",
    theme,
    stylesheetUrls = [],
    cacheKey = "pdf-generator",
    header,
    footer,
    page
  } = args

  const normalizedHeader = normalizePdfHeaderSlot(header)
  const normalizedFooter = normalizePdfFooterSlot(footer)

  const cache = createPdfEmotionCache(cacheKey)

  const documentTree = (
    <CacheProvider value={cache}>
      <TssCacheProvider value={cache}>
        {theme ? (
          <ThemeProvider theme={theme}>
            {renderRunningElement(normalizedHeader)}
            <div id="pdf-generator-root">{content}</div>
            {renderRunningElement(normalizedFooter)}
          </ThemeProvider>
        ) : (
          <>
            {renderRunningElement(normalizedHeader)}
            <div id="pdf-generator-root">{content}</div>
            {renderRunningElement(normalizedFooter)}
          </>
        )}
      </TssCacheProvider>
    </CacheProvider>
  )

  const bodyHtml = ReactDOMServer.renderToString(documentTree)
  const emotionCss = buildEmotionStyleTagsFromHtml(bodyHtml, cache)

  const pageLayoutCss = buildPdfPageLayoutCss({
    page,
    header: normalizedHeader,
    footer: normalizedFooter
  })

  const stylesheetMarkup = stylesheetUrls
    .map(
      href =>
        `<link rel="stylesheet" href="${escapeHtml(href)}" type="text/css" />`
    )
    .join("\n")

  return `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${escapeHtml(documentTitle)}</title>
        ${stylesheetMarkup}
        <style>${pageLayoutCss}</style>
        ${emotionCss}
      </head>
      <body>
        ${bodyHtml}
      </body>
    </html>`
}
